import * as THREE from 'three';
import type { SystemElement } from '../data/types';
import { BY_ID } from '../data/system';

const LINE_COLOR = new THREE.Color(0x2f4a63);

export interface ConnectionLine {
  fromId: string;
  toId: string;
  line: THREE.Line;
  material: THREE.LineBasicMaterial;
}

/**
 * Build curved connection lines for an element to all its related elements.
 * Lines are added to the scene with opacity 0; hover handler animates them in.
 */
export function buildConnectionsFor(
  el: SystemElement,
  scene: THREE.Scene,
): ConnectionLine[] {
  const lines: ConnectionLine[] = [];
  const from = new THREE.Vector3(...el.position);

  for (const relId of el.related) {
    const rel = BY_ID[relId];
    if (!rel) continue;
    const to = new THREE.Vector3(...rel.position);

    const mid = from.clone().add(to).multiplyScalar(0.5);
    const dist = from.distanceTo(to);
    // Lift the midpoint perpendicular to the line — gives a graceful arc
    // through 3D space rather than a flat segment.
    const lift = Math.min(2.4, dist * 0.18);
    // Bias upward and slightly outward from origin, so curves rise
    // through the composition rather than crossing under it.
    const upward = new THREE.Vector3(0, 1, 0);
    const radial = mid.clone().normalize();
    mid.add(upward.multiplyScalar(lift));
    mid.add(radial.multiplyScalar(lift * 0.25));

    const curve = new THREE.QuadraticBezierCurve3(from, mid, to);
    const points = curve.getPoints(40);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: LINE_COLOR,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 1;
    scene.add(line);

    lines.push({ fromId: el.id, toId: relId, line, material });
  }

  return lines;
}

export class ConnectionLayer {
  private cache = new Map<string, ConnectionLine[]>();
  private active: ConnectionLine[] = [];
  private targetOpacity = 0;

  constructor(private scene: THREE.Scene) {}

  showFor(el: SystemElement) {
    const cached = this.cache.get(el.id);
    if (cached) {
      this.active = cached;
    } else {
      const built = buildConnectionsFor(el, this.scene);
      this.cache.set(el.id, built);
      this.active = built;
    }
    this.targetOpacity = 0.55;
  }

  hide() {
    this.targetOpacity = 0;
  }

  update() {
    // Ease all active lines toward target; non-active lines fade out.
    const all = [...this.cache.values()].flat();
    for (const cl of all) {
      const target = this.active.includes(cl) ? this.targetOpacity : 0;
      const o = cl.material.opacity;
      cl.material.opacity = o + (target - o) * 0.18;
    }
    if (this.targetOpacity === 0 && this.active.length > 0) {
      // Allow fade out, then clear active set.
      const maxOpacity = Math.max(0, ...this.active.map((l) => l.material.opacity));
      if (maxOpacity < 0.01) this.active = [];
    }
  }
}
